import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import StyledModal from '../../../Components/GenericModal/StyledModal';
import { styles } from '../../styles';
import ModalFooterButtons from '../../../Components/GenericModal/ModalFooterButtons';
import { TAcceptedRecordTypes } from '../../types';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Api from '../../API';
import GenericDialog from '../../../Components/Modals/GenericDialog';
import { DebouncedFormTextField } from '../../../Components/CustomUIElements/FormTextField';

export default function NewTagModal({
  modalOpen,
  onClose,
  selectedTag,
  editingTag,
  setSnackbar
}: {
  modalOpen: boolean;
  onClose: () => void;
  selectedTag: TAcceptedRecordTypes | null;
  editingTag?: boolean;
  setSnackbar: ({
    message,
    state
  }: {
    message: string;
    state: 'success' | 'warning' | 'error';
  }) => void;
}) {
  const queryClient = useQueryClient();
  const [tagName, setTagName] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const handleClose = () => setConfirmationModalOpen(true);

  const { mutate: submit, isLoading: submitting } = useMutation({
    mutationFn: async () => {
      const params = { name: tagName };
      if (editingTag && selectedTag) {
        await Api.updateTag(selectedTag.id, params);
      } else {
        await Api.newTag(params);
      }
    },
    onSuccess: () => {
      setSnackbar({
        message: `Tag ${editingTag ? 'updated' : 'created'} successfully`,
        state: 'success' as const
      });
      onClose();
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
    onError: (error) => {
      if (error.code === 422 && error.message === 'Tag name already exists') {
        setErrors({ tagName: 'Tag name already exists' });
      } else {
        setSnackbar({
          message: `There was an error in ${editingTag ? 'updating' : 'creating'} the Tag: ${error.message || error}`,
          state: 'error' as const
        });
      }
    }
  });

  const validateInputs = () => {
    const errors: Record<string, string> = {};
    if (!tagName) {
      errors.tagName = 'Tag name is required';
    } else {
      if (!editingTag && validateTagNameData?.error) {
        errors.tagName = 'Tag name already exists';
      }
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const { data: validateTagNameData } = useQuery({
    queryKey: [tagName],
    queryFn: async () => {
      const { res } = await Api.validateTagName({ name: tagName });
      return res;
    }
  });

  useEffect(() => {
    if (selectedTag) {
      if ('name' in selectedTag) {
        setTagName(selectedTag.name);
      }
    }
  }, [selectedTag]);

  return (
    <StyledModal
      isOpen={modalOpen}
      label="New Tag modal"
      handleClose={handleClose}
      styleOverrides={styles.modalStyleOverrides}
    >
      <Stack sx={styles.modalContainer}>
        <Stack sx={styles.modalTitle}>{`${editingTag ? 'Edit' : 'New'} Tag`}</Stack>
        <Stack sx={{ rowGap: 2, overflow: 'auto' }}>
          <DebouncedFormTextField
            label=" Tag name"
            defaultValue={tagName}
            onDebouncedChange={(value: string) => setTagName(value)}
            debounceTime={300}
            styles={{ width: '48%' }}
            required
            error={errors.tagName}
          />
        </Stack>
        <ModalFooterButtons
          primaryButtonText={editingTag ? 'Update' : 'Confirm'}
          secondaryButtonText="Cancel"
          secondaryButtonCallback={handleClose}
          primaryButtonMinWidth="95px"
          primaryButtonCallback={() => validateInputs() && submit()}
          isLoading={submitting}
        />
      </Stack>
      <GenericDialog
        isDialogOpen={confirmationModalOpen}
        setDialogOpen={setConfirmationModalOpen}
        title="Close without saving?"
        description={
          <Stack>
            <Stack>Are you sure you want to cancel?</Stack>
            <Stack>Unsaved changes will be lost.</Stack>
          </Stack>
        }
        buttonText="Yes, close"
        secondaryButtonText="Back to editing"
        url=""
        buttonCallback={() => {
          onClose();
        }}
        callbackLoading={false}
      />
    </StyledModal>
  );
}
